import { Intent } from '@blueprintjs/core';
import { Analog } from '@retro-ui/api-contract';
import { useState } from 'react';

import { apiClient } from '@f/api-client';
import { AppToaster } from '@f/components/notification-toaster/AppToaster';
import { useIsDebugModeOn } from '@f/hooks/useIsDebugModeOn';

export type AnalogsProps = ReturnType<typeof useAnalogs>;

export const PROMPT_BEFORE_LOSING_ANALOGS = 'This action will result in losing the generated analogs. Are you sure?';

interface UseAnalogsOptions {
  smiles: string;
}
export function useAnalogs(options: UseAnalogsOptions) {
  const { smiles } = options;

  const isDebugModeOn = useIsDebugModeOn();

  const [isGeneratingAnalogs, setGeneratingAnalogs] = useState(false);
  const [isFetchingAnalogExplanations, setFetchingAnalogExplanations] = useState(false);
  const [analogsPrompt, setAnalogsPrompt] = useState('');

  const [analogs, setAnalogs] = useState<Analog[] | null>(null);
  const [selectedAnalogs, setSelectedAnalogs] = useState<Analog[]>([]);

  const [analogsGenerationTime, setAnalogsGenerationTime] = useState<number | null>(null);
  const [analogsDebugInfo, setAnalogsDebugInfo] = useState<string | null>(null);

  // const [locationAnalogPrompts, setLocationAnalogPrompts] = useState<LocationAnalogPrompt[]>([]);

  const resetAnalogState = () => {
    setAnalogsPrompt('');
    setAnalogs(null);
    setSelectedAnalogs([]);
    setAnalogsGenerationTime(null);
    setAnalogsDebugInfo(null);
  };

  const selectAllAnalogs = () => {
    setSelectedAnalogs(analogs ?? []);
  };
  const selectNoAnalogs = () => {
    setSelectedAnalogs([]);
  };

  // const addLocationPrompt = (locationAnalogPrompt: LocationAnalogPrompt) => {
  //   const existingLocation = locationAnalogPrompts.find(p => isEqual(p.location, locationAnalogPrompt.location));
  //   if (existingLocation) {
  //     return;
  //   }
  //   setLocationAnalogPrompts(prev => [...prev, locationAnalogPrompt]);
  // };

  // const pruneLocationPrompts = () => {
  //   setLocationAnalogPrompts(prev => prev.filter(({ prompt }) => prompt.length > 0));
  // };

  // const updateLocationPrompt = (idx: number, prompt: string) => {
  //   setLocationAnalogPrompts(prev =>
  //     prev.map((prevPrompt, prevIdx) => {
  //       if (prevIdx === idx) {
  //         return { ...prevPrompt, prompt };
  //       }
  //       return prevPrompt;
  //     })
  //   );
  // };

  // const removeLocationPrompt = (idx: number) => {
  //   setLocationAnalogPrompts(prev => prev.filter((prevPrompt, prevIdx) => prevIdx !== idx));
  // };

  // const locationAnalogPromptNodes = locationAnalogPrompts.map((locationAnalogPrompt, idx) => (
  //   <LocationAnalogPromptNode
  //     key={idx}
  //     {...locationAnalogPrompt}
  //     setPrompt={prompt => {
  //       updateLocationPrompt(idx, prompt);
  //     }}
  //     removePrompt={() => {
  //       removeLocationPrompt(idx);
  //     }}
  //   />
  // ));

  const generateAnalogs = async () => {
    setGeneratingAnalogs(true);
    try {
      const startTime = performance.now();
      const { body, status } = await apiClient.analog.generateAnalogs({
        body: { smiles, guidance: analogsPrompt },
      });
      if (status !== 200) {
        throw new Error(`Fetch to generate analogs failed with status ${status}`);
      }
      const { analogs, debugInfo } = body;
      const endTime = performance.now();

      setAnalogsGenerationTime(endTime - startTime);
      setAnalogsDebugInfo(debugInfo ?? null);
      setAnalogs(analogs);
      setSelectedAnalogs(analogs);

      fetchAnalogDescriptions(analogs);
    } catch (error) {
      console.log('Analogs fetch failed', error);
      AppToaster.show({
        intent: Intent.DANGER,
        message: `Failed to generate analogs. Please try again.`,
      });
    } finally {
      setGeneratingAnalogs(false);
    }
  };

  const fetchAnalogDescriptions = async (analogs: Analog[]) => {
    try {
      setFetchingAnalogExplanations(true);
      const { body, status } = await apiClient.analog.generateAnalogExplanations({
        body: { smiles, guidance: analogsPrompt, analogs: analogs.map(analog => analog.smiles) },
      });
      if (status !== 200) {
        return;
      }
      const analogsWithExplanation = body;
      setFetchingAnalogExplanations(false);

      setAnalogs(prev =>
        prev
          ? prev.map(analog => {
              const analogWithExplanation = analogsWithExplanation.find(a => a.smiles === analog.smiles);
              if (analogWithExplanation) {
                return {
                  ...analog,
                  ...analogWithExplanation,
                };
              }
              return analog;
            })
          : null
      );
      setSelectedAnalogs(prev =>
        prev.map(analog => {
          const analogWithExplanation = analogsWithExplanation.find(a => a.smiles === analog.smiles);
          if (analogWithExplanation) {
            return {
              ...analog,
              ...analogWithExplanation,
            };
          }
          return analog;
        })
      );
    } catch (error) {
      console.log('Analogs explanation fetch failed', error);
    } finally {
      setGeneratingAnalogs(false);
    }
  };

  return {
    analogs,
    setAnalogs,
    isGeneratingAnalogs,
    generateAnalogs,
    selectedAnalogs,
    setSelectedAnalogs,
    analogsPrompt,
    setAnalogsPrompt,
    isFetchingAnalogExplanations,
    resetAnalogState,
    selectAllAnalogs,
    selectNoAnalogs,
    debugInfo: isDebugModeOn ? (
      <>
        <p>Generation took {analogsGenerationTime ? `${(analogsGenerationTime / 1000).toFixed(2)}s` : 'unknown'}</p>
        {analogsDebugInfo && (
          <p>
            Debug info: <br />
            {analogsDebugInfo}
          </p>
        )}
      </>
    ) : null,
  } as const;
}
